import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}

const Animals = [
    "cow.png", "dog.png", "cat.png", "horse.png", "goat.png", "sheep.png", "pig.png", "lion.png", "tiger.png", "elephant.png",
    "rhino.png", "giraffe.png", "zebra.png", "snake.png", "cheetah.png", "leopard.png", "wolf.png", "fox.png", "bear.png", "gorilla.png"
];

const MatchAnimal: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');

    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {

        const shuffledAnimals = [...Animals].sort(() => Math.random() - 0.5);
        const selectedAnimals = shuffledAnimals.slice(0, 4);
        const shuffledRightSideAnimals = shuffleArray([...selectedAnimals]);
        const pairs = selectedAnimals.map((animal, index) => {
            return {
                left_id: index + 1,
                leftImage: animal,
                rightImage: shuffledRightSideAnimals[index],
                right_id: selectedAnimals.indexOf(shuffledRightSideAnimals[index]) + 1
            };
        });

        setQuestions(pairs);
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Match the Animal – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        resetConnections()
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 3000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids learn about animals with the "Match the Animal" activity on Mamas Guidance—an engaging and educational exercise where children match animals with their names, sounds, or homes to enhance early science and recognition skills.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Match the Animal</h1>

                                <p>
                                    Help your child learn about animals with this engaging <strong>Match the Animal</strong> worksheet! This nursery-level EVS activity allows children to <strong>connect animals with their names, homes, or features</strong> through a simple and interactive matching task.
                                </p>

                                <h2>🦊 What’s in the Worksheet?</h2>
                                <p>
                                    This worksheet features familiar animals like <b><em>dog, elephant, cat, fish</em></b> and others. Kids are asked to match each animal picture to its:
                                </p>
                                <ul>
                                    <li>Name</li>
                                </ul>
                                <p>
                                    The colorful visuals and clear lines for drawing connections make this activity exciting and educational.
                                </p>

                                <h2>🎯 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Animal <strong>identification</strong> and <strong>recognition</strong></li>
                                    <li>Associating animals with their names</li>
                                    <li>Developing hand-eye coordination</li>
                                    <li>Enhancing <strong>environmental awareness</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Use</h2>
                                <ul>
                                    <li>Ask your child to name the animals on the left.</li>
                                    <li>Guide them to read or identify what’s on the right.</li>
                                    <li>Help them draw a line or use a crayon to match them.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/environmental-science/vegetables/match-vegetable"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Vegetable</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/fruit/match-fruit"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Fruit</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/body-parts/match-bodyparts"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Body Parts</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Matching activities build <strong>logical thinking</strong> and help reinforce early <strong>science concepts</strong>. With this worksheet, children understand relationships in nature — like who lives where, and what each animal looks like.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Match the Animal</h1>

                            <p>
                                Help your child learn about animals with this engaging <strong>Match the Animal</strong> worksheet! This nursery-level EVS activity allows children to <strong>connect animals with their names, homes, or features</strong> through a simple and interactive matching task.
                            </p>

                            <h2>🦊 What’s in the Worksheet?</h2>
                            <p>
                                This worksheet features familiar animals like <em>dog, elephant, cat, fish</em> and others. Kids are asked to match each animal picture to its:
                            </p>
                            <ul>
                                <li>Name</li>
                                <li>Home (like kennel, nest, jungle)</li>
                                <li>Food or characteristic (optional variations)</li>
                            </ul>
                            <p>
                                The colorful visuals and clear lines for drawing connections make this activity exciting and educational.
                            </p>

                            <h2>🎯 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Animal identification and recognition</li>
                                <li>Associating animals with their names or homes</li>
                                <li>Developing hand-eye coordination</li>
                                <li>Enhancing environmental awareness</li>
                            </ul>

                            <h2>👩‍🏫 How to Use</h2>
                            <ul>
                                <li>Ask your child to name the animals on the left.</li>
                                <li>Guide them to read or identify what’s on the right (names/homes/etc.).</li>
                                <li>Help them draw a line or use a crayon to match them.</li>
                                <li>Encourage conversations like “Where does a fish live?” or “What does a lion eat?”</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/environmental-science/vegetables/match-vegetable"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Match Vegetable</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/environmental-science/fruit/match-fruit"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Match Fruit</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/environmental-science/body-parts/match-bodyparts"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Match Body Parts</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Important</h2>
                            <p>
                                Matching activities build logical thinking and help reinforce early science concepts. With this worksheet, children understand relationships in nature — like who lives where, and what each animal looks like.
                            </p>
                        </article>
                        <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                            {/* SVG for drawing lines */}
                            <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                                {connections.map((conn, index) => (
                                    <line
                                        key={index}
                                        x1={conn.x1}
                                        y1={conn.y1}
                                        x2={conn.x2}
                                        y2={conn.y2}
                                        stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                        strokeWidth="3"
                                    />
                                ))}
                            </svg>


                            <h6>{question}</h6>
                            {/* Left & Right columns under SVG */}
                            <div className="columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%",height: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div
                                            key={item.left_id}
                                            className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                            onClick={(e) => handleLeftClick(e, item.left_id)}
                                            style={{ cursor: "pointer", padding: "10px" }}
                                        >
                                            <img className="img-thumbnail1" src={process.env.REACT_APP_API_URL + item.leftImage} alt="Left Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>

                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img className="img-thumbnail1" src={process.env.REACT_APP_API_URL + item.rightImage} alt="Right Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-4 text-center" style={{ display: 'flex', gap: '10px' }}>
                                <div className="optionButton background-blue" style={{ fontSize: '18px' }} onClick={resetConnections}>
                                    Reset
                                </div>
                                <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next &gt;&gt;</div>
                            </div>

                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MatchAnimal;
